import { Box, SxProps, Theme } from '@mui/material';
import { useMemo } from 'react';

import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { Callout } from '@/components/notifications/Callout/Callout';
import { DocumentWhereInput } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { DocumentRepresentation } from './DocumentRepresentation';
import {
  GetDocumentRepresentationListDocumentsDocument,
  useGetDocumentRepresentationListDocumentsQuery,
} from './graphql/DocumentRepresentationList.generated';

export interface DocumentRepresentationListProps {
  documentsLike?: DocumentWhereInput;
  showDelete?: boolean;
  sx?: SxProps<Theme>;
  // If documentIds is passed, this list will be used when querying for documents
  // If an empty array is passed, the query for documents will not be made
  // If not passed, the documentsLike will be used to query for documents
  documentIds?: string[];
}

// Use this to force a requery
export const RequeryDocumentRepresentationListDocuments =
  GetDocumentRepresentationListDocumentsDocument;

export function useGetDocumentsForList({
  documentsLike,
}: {
  documentsLike: DocumentWhereInput | null;
}) {
  const noDocs = documentsLike?.idIn?.length === 0 || !documentsLike;

  const queryProps = useGetDocumentRepresentationListDocumentsQuery({
    fetchPolicy: 'cache-and-network',
    skip: noDocs,
    variables: {
      documentsLike: documentsLike!,
    },
  });

  const data = queryProps.data;

  const documents = useMemo(() => {
    if (data) {
      return getNodes(data.documents);
    }

    return [];
  }, [data]);

  return {
    documents,
    ...queryProps,
  };
}

export function DocumentRepresentationList({
  documentsLike,
  showDelete,
  sx,
  documentIds,
}: DocumentRepresentationListProps) {
  const { disabled: disabledFromContext } = useFormFieldsDisabled();
  // query by documentIds if they are provided, otherwise query by documentsLike
  let input: DocumentWhereInput | null = null;

  if (documentIds) {
    input = { idIn: documentIds };
  } else if (documentsLike) {
    input = documentsLike;
  }

  const { documents, error } = useGetDocumentsForList({
    documentsLike: input,
  });

  if (error) {
    return (
      <Callout severity="error">
        Sorry, we weren&apos;t able to load these documents. Please refresh the
        page to try again.
      </Callout>
    );
  }

  return (
    <Box sx={sx}>
      {documents?.map((document, i) => {
        return (
          <Box mt={i === 0 ? 0 : 1} key={document.id}>
            <DocumentRepresentation
              key={document.id}
              showDelete={showDelete && !disabledFromContext}
              fileName={document.name}
              documentType={document.type}
              uploadedAt={document.file.createdAt}
              uploadedBy={document.file.user.displayName}
              documentId={document.id}
            />
          </Box>
        );
      })}
    </Box>
  );
}
